import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { AccessTag, UserType } from '../api/types';
import { PageLoader } from '../components/ui';
import { SESSION_ENDED_NOTICE, hasAccess, homeFor, useAuth } from './AuthContext';

/**
 * Route wrapper for signed-in pages. Anonymous visitors go to /sign-in and
 * come back to the page they wanted after signing in; a user of the wrong
 * kind (tenant on /staff, staff on /portal) is sent to their own home, as is
 * one without the required access tag (`access="admin"` for the Users page).
 */
export function RequireAuth({ role, access }: { role?: UserType; access?: AccessTag }) {
  const { state } = useAuth();
  const location = useLocation();
  if (state.status === 'loading') return <PageLoader label="Restoring your session…" />;
  if (state.status === 'anonymous') {
    const notice = state.reason === 'session-expired' ? SESSION_ENDED_NOTICE : undefined;
    return <Navigate to="/sign-in" replace state={{ from: location.pathname, notice }} />;
  }
  if (state.me.user.isDeactivated) {
    return location.pathname === '/deactivated' ? (
      <Outlet />
    ) : (
      <Navigate to="/deactivated" replace />
    );
  }
  if (location.pathname === '/deactivated') return <Navigate to={homeFor(state.me)} replace />;
  if (role && state.me.principal.actorKind !== role) {
    return <Navigate to={homeFor(state.me)} replace />;
  }
  if (access && !hasAccess(state.me, access)) {
    return <Navigate to={homeFor(state.me)} replace />;
  }
  return <Outlet />;
}

/** Wraps the public auth pages: a signed-in user is sent straight to their dashboard. */
export function RedirectIfAuthenticated() {
  const { state } = useAuth();
  if (state.status === 'loading') return <PageLoader label="Restoring your session…" />;
  if (state.status === 'authenticated') return <Navigate to={homeFor(state.me)} replace />;
  return <Outlet />;
}
